import { InteractionStatus, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useState } from 'react';
import { apiWrapper } from '../../config/api-wrapper';
import { acdpGroupsPrefix } from '../../config/env';
import { loginRequest, msalConfig } from '../../config/msal-config';
import AuthContext from './auth-context';
import { SingInOptions } from './auth-types';
import authService from './auth.service';
import { User } from './user-model';

const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and handle redirect promise
msalInstance.initialize().then(() => {
  msalInstance.handleRedirectPromise().catch(error => {
    console.error('Error handling redirect:', error);
  });
});

function AuthProviderContent({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState<User | undefined>();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  const signIn = useCallback(
    async (options: SingInOptions): Promise<void> => {
      try {
        await instance.loginRedirect({
          ...loginRequest,
        });
      } catch (error) {
        console.error('Login error:', error);
      }
    },
    [instance],
  );

  const signOut = useCallback((): void => {
    void instance.logoutRedirect({
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    });
  }, [instance]);

  useEffect(() => {
    const handleAuthentication = async () => {
      if (inProgress !== InteractionStatus.None) {
        setIsAuthenticated(null);
        return;
      }

      const currentAccount = accounts?.[0] ?? null;

      if (!currentAccount) {
        setIsAuthenticated(false);
        setUser(undefined);
        apiWrapper.clearTokens();
        authService.msalAuthState = { isAuthenticated: false, account: null };
        sessionStorage.removeItem('userStored');
        return;
      }

      try {
        // Acquire token silently for user info (Graph API)
        const tokenResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: currentAccount,
        });

        const idToken = tokenResponse.idToken;
        const apiAccessToken = tokenResponse.accessToken;

        // Set tokens in API wrapper - use API token for Authorization
        apiWrapper.setTokens(apiAccessToken, idToken, '');

        // Update auth service state with API token
        authService.msalAuthState = {
          isAuthenticated: true,
          account: currentAccount,
          accessToken: apiAccessToken,
          idToken,
        };

        // Extract user information
        const { username, name } = currentAccount;
        let userGroup = '';

        // Try to extract groups from ID token claims
        if (tokenResponse.idTokenClaims) {
          const claims = tokenResponse.idTokenClaims as any;

          // Try different possible group claim names
          const groupsClaim = claims.groups || claims[acdpGroupsPrefix] || claims['roles'];

          if (groupsClaim && Array.isArray(groupsClaim) && groupsClaim.length > 0) {
            userGroup = groupsClaim[0];
          }
        }

        const userEmail = currentAccount.idTokenClaims?.email as string;
        const user = {
          id: username.split('@')[0] || '',
          name: name || '',
          email: userEmail || '',
          userGroup,
        };

        setUser(user);
        setIsAuthenticated(true);

        const accountId = currentAccount.localAccountId;
        const storedAccountId = sessionStorage.getItem('userStored');

        if (storedAccountId !== accountId) {
          sessionStorage.setItem('userStored', accountId);
          try {
            await authService.storeUser(username.split('@')[0], userEmail || '', name || username || '');
          } catch (error) {
            console.error('Error storing user in database:', error);
            sessionStorage.removeItem('userStored');
          }
        }
      } catch (error) {
        console.error('Token acquisition error:', error);
        setIsAuthenticated(false);
        setUser(undefined);
        apiWrapper.clearTokens();
      }
    };

    handleAuthentication();
  }, [accounts, inProgress, instance]);

  return <AuthContext.Provider value={{ user, isAuthenticated, signIn, signOut }}>{children}</AuthContext.Provider>;
}

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderContent>{children}</AuthProviderContent>
    </MsalProvider>
  );
}
